export interface FAQ {
  question: string
  answer: string
  category: string
}

export interface FAQTranslations {
  title: string
  subtitle: string
  categories: string[]
  faqs: FAQ[]
  contactText: string
  contactLink: string
}

export const faqTranslations: Record<string, FAQTranslations> = {
  en: {
    title: 'Frequently Asked Questions',
    subtitle: 'Find answers to common questions about our gardening services',
    categories: ['All', 'General', 'Tree Surgery', 'Fencing', 'Pressure Washing'],
    faqs: [
      // General Questions
      {
        question: 'What areas do you service?',
        answer: 'I provide my services in Barry, Penarth, Dinas Powys, Cardiff, Bridgend, and surrounding areas in South Wales.',
        category: 'General'
      },
      {
        question: 'How often should I have my garden maintained?',
        answer: 'I recommend regular maintenance visits every 2-4 weeks during the growing season (March to October) and monthly visits during winter. However, this can be adjusted based on your garden\'s specific needs and your preferences.',
        category: 'General'
      },
      {
        question: 'Do you provide free quotes?',
        answer: 'Yes, I offer free, no-obligation quotes for all my services. I\'ll visit your garden to assess its needs and provide a detailed quote based on your requirements.',
        category: 'General'
      },
      {
        question: 'Are you insured?',
        answer: 'Yes, I am fully insured with public liability insurance to protect both my clients and myself while working on your property.',
        category: 'General'
      },
      {
        question: 'Do I need to provide any equipment?',
        answer: 'No, I bring all necessary equipment and tools to complete the job. This includes lawn mowers, hedge trimmers, and other gardening equipment.',
        category: 'General'
      },
      {
        question: 'What happens to the garden waste?',
        answer: 'I remove all garden waste from your property and dispose of it responsibly. I recycle where possible and ensure proper disposal of all materials.',
        category: 'General'
      },
      {
        question: 'What\'s your cancellation policy?',
        answer: 'I understand that sometimes you need to reschedule. I ask for at least 24 hours notice for cancellations or rescheduling. I\'m flexible and will work with you to find a suitable alternative time.',
        category: 'General'
      },
      {
        question: 'Do you work in all weather conditions?',
        answer: 'I work in most weather conditions, but I may need to reschedule if conditions are unsafe (heavy rain, high winds, or extreme cold). Your safety and the quality of my work are my priorities.',
        category: 'General'
      },
      {
        question: 'How do you handle emergencies?',
        answer: 'For emergency situations like storm damage or fallen trees, I offer emergency call-out services. Contact me immediately and I\'ll respond as quickly as possible to assess and address the situation.',
        category: 'General'
      },
      {
        question: 'Can you work around my schedule?',
        answer: 'Absolutely! I offer flexible scheduling including early mornings, evenings, and weekends. I\'ll work with you to find times that suit your schedule and minimize disruption.',
        category: 'General'
      },
      {
        question: 'What types of clients do you work with?',
        answer: 'I work with a wide range of clients, including homeowners, landlords, property management companies, estate agents, hotels, housing associations, and facilities management companies. Whether you have a private garden or manage multiple properties, I can provide tailored gardening services to suit your needs.',
        category: 'General'
      },
      // Tree Surgery Questions
      {
        question: 'Do you need permission to remove trees?',
        answer: 'It depends on the tree and your location. Some trees may be protected by Tree Preservation Orders (TPOs) or be in conservation areas. I\'ll check this for you and help with any necessary applications before starting work.',
        category: 'Tree Surgery'
      },
      {
        question: 'How much does tree surgery cost?',
        answer: 'Tree surgery costs vary depending on the size, location, and complexity of the work. I provide free, detailed quotes after assessing your specific situation. Factors include tree size, access, and disposal requirements.',
        category: 'Tree Surgery'
      },
      {
        question: 'Can you remove large trees safely?',
        answer: 'Yes, I have the expertise and equipment to safely remove trees of all sizes. For large trees, I use specialized techniques and equipment to ensure safe removal without damage to your property.',
        category: 'Tree Surgery'
      },
      {
        question: 'What happens to the tree stump after removal?',
        answer: 'I offer stump grinding services to remove tree stumps completely. This prevents regrowth and allows you to use the space for other purposes. I can also leave the stump if you prefer, or cut it to ground level.',
        category: 'Tree Surgery'
      },
      {
        question: 'How long does tree surgery take?',
        answer: 'The duration depends on the size and complexity of the work. Small pruning jobs might take a few hours, while large tree removals could take a full day. I\'ll give you a realistic timeframe when I quote.',
        category: 'Tree Surgery'
      },
      // Fencing Questions
      {
        question: 'How long does fence installation take?',
        answer: 'Installation time varies based on the fence type and length. A typical garden fence (20-30 meters) usually takes 1-2 days. I\'ll provide a specific timeline when I assess your project.',
        category: 'Fencing'
      },
      {
        question: 'What types of fencing materials do you work with?',
        answer: 'I work with a wide range of materials including wood, metal, vinyl, and composite fencing. I can advise on the best option for your needs, considering factors like durability, maintenance, and budget.',
        category: 'Fencing'
      },
      {
        question: 'Do you need planning permission for fences?',
        answer: 'Most garden fences don\'t require planning permission, but there are height restrictions (usually 2 meters). If your fence is higher or in a conservation area, I\'ll help you check the requirements.',
        category: 'Fencing'
      },
      {
        question: 'Can you repair existing fences?',
        answer: 'Yes, I can repair most types of fencing. I\'ll assess the damage and recommend the best approach - whether it\'s a simple repair or if replacement would be more cost-effective in the long run.',
        category: 'Fencing'
      },
      {
        question: 'How long will my new fence last?',
        answer: 'Fence lifespan depends on the material and maintenance. Wooden fences typically last 10-15 years with proper treatment, while metal and vinyl fences can last 20+ years. I\'ll advise on maintenance to maximize longevity.',
        category: 'Fencing'
      },
      // Pressure Washing Questions
      {
        question: 'Is pressure washing safe for all surfaces?',
        answer: 'I use appropriate pressure levels for different surfaces to avoid damage. I\'m experienced in cleaning driveways, patios, decking, fences, and other outdoor surfaces safely and effectively.',
        category: 'Pressure Washing'
      },
      {
        question: 'How often should I pressure wash my driveway/patio?',
        answer: 'I recommend pressure washing driveways and patios annually to maintain their appearance and prevent algae/moss buildup. High-traffic areas or those in shaded locations may need more frequent cleaning.',
        category: 'Pressure Washing'
      },
      {
        question: 'Do you use eco-friendly cleaning products?',
        answer: 'Yes, I use environmentally friendly cleaning solutions where possible. I can discuss your preferences and use products that are safe for your garden, pets, and the environment.',
        category: 'Pressure Washing'
      },
      {
        question: 'How long does pressure washing take to dry?',
        answer: 'Drying time depends on weather conditions and surface type. Typically, surfaces are dry within 2-4 hours in good weather. I\'ll advise on the best time to schedule the work.',
        category: 'Pressure Washing'
      },
      {
        question: 'Can pressure washing damage my plants?',
        answer: 'I take care to protect your plants during pressure washing. I cover nearby plants and use appropriate pressure levels to avoid damage. I\'ll discuss any concerns before starting the work.',
        category: 'Pressure Washing'
      }
    ],
    contactText: 'Still have questions?',
    contactLink: 'Contact me'
  },
  cy: {
    title: 'Cwestiynau Cyffredin',
    subtitle: 'Dewch o hyd i atebion i gwestiynau cyffredin am fy ngwasanaethau garddio',
    categories: ['Y Cyfan', 'Cyffredinol', 'Llawfeddygaeth Coed', 'Ffensio', 'Golchi Pwysedd'],
    faqs: [
      // General Questions
      {
        question: 'Pa ardaloedd ydych chi\'n gwasanaethu?',
        answer: 'Rwy\'n darparu fy ngwasanaethau ym Mhri, Penarth, Dinas Powys, Caerdydd, Pen-y-bont ar Ogwr, ac ardaloedd cyfagos yn De Cymru.',
        category: 'Cyffredinol'
      },
      {
        question: 'Pa mor aml ddylwn i gael cynnal a chadw fy ngardd?',
        answer: 'Rwy\'n argymell ymweliadau cynnal a chadw rheolaidd bob 2-4 wythnos yn ystod y tymor tyfu (Mawrth hyd Hydref) ac ymweliadau misol yn ystod y gaeaf. Fodd bynnag, gellir addasu hyn yn seiliedig ar anghenion penodol eich gardd a\'ch dewisiadau.',
        category: 'Cyffredinol'
      },
      {
        question: 'Ydych chi\'n darparu dyfynbris am ddim?',
        answer: 'Ydy, rwy\'n cynnig dyfynbris am ddim, heb rwymedigaeth ar gyfer fy holl wasanaethau. Byddaf yn ymweld â\'ch gardd i asesu ei hanghenion a darparu dyfynbris manwl yn seiliedig ar eich gofynion.',
        category: 'Cyffredinol'
      },
      {
        question: 'Ydych chi\'n yswirio?',
        answer: 'Ydy, rydw i wedi fy hyswirio\'n llawn gydag yswiriant atebolrwydd cyhoeddus i ddiogelu fy nghleientiaid a fi fy hun wrth weithio ar eich eiddo.',
        category: 'Cyffredinol'
      },
      {
        question: 'Oes angen i mi ddarparu unrhyw offer?',
        answer: 'Na, rwy\'n dod â\'r holl offer ac offerau angenrheidiol i gwblhau\'r swydd. Mae hyn yn cynnwys peiriannau torri gwair, peiriannau trimio gwrych, ac offer garddio eraill.',
        category: 'Cyffredinol'
      },
      {
        question: 'Beth sy\'n digwydd i\'r gwastraff gardd?',
        answer: 'Rwy\'n tynnu\'r holl wastraff gardd o\'ch eiddo a\'i waredu\'n gyfrifol. Rwy\'n ailgylchu lle bo\'n bosibl ac yn sicrhau gwaredu priodol o\'r holl ddeunyddiau.',
        category: 'Cyffredinol'
      },
      {
        question: 'Beth yw eich polisi canslo?',
        answer: 'Rwy\'n deall bod angen ail-drefnu weithiau. Gofynnaf am rybudd o leiaf 24 awr ar gyfer canslo neu ail-drefnu. Rwy\'n hyblyg a byddaf yn gweithio gyda chi i ddod o hyd i amser addas arall.',
        category: 'Cyffredinol'
      },
      {
        question: 'Ydych chi\'n gweithio ym mhob amodau tywydd?',
        answer: 'Rwy\'n gweithio ym mhob amodau tywydd mwyaf, ond efallai y bydd angen ail-drefnu os yw\'r amodau\'n anfelys (glaw trwm, gwyntoedd cryf, neu oer eithafol). Eich diogelwch a ansawdd fy ngwaith yw fy mlaenoriaethau.',
        category: 'Cyffredinol'
      },
      {
        question: 'Sut ydych chi\'n ymdrin ag argyfyngau?',
        answer: 'Ar gyfer sefyllfaoedd brys fel difrod storm neu goed sy\'n cwympo, rydw i\'n cynnig gwasanaethau galwadau brys. Cysylltwch â mi ar unwaith a byddaf yn ymateb cyn gynted â phosibl i asesu a mynd i\'r afael â\'r sefyllfa.',
        category: 'Cyffredinol'
      },
      {
        question: 'Allwch chi weithio o amgylch fy amserlen?',
        answer: 'Yn bendant! Rwy\'n cynnig amserlennu hyblyg gan gynnwys boreau cynnar, nosweithiau, a phenwythnosau. Byddaf yn gweithio gyda chi i ddod o hyd i amseroedd sy\'n addas i\'ch amserlen a lleihau\'r tarfu.',
        category: 'Cyffredinol'
      },
      {
        question: 'Pa fathau o gleientiaid ydych chi\'n gweithio gyda nhw?',
        answer: 'Rwy\'n gweithio gyda llawer o wahanol gleientiaid, gan gynnwys perchnogion tai, landlordiaid, cwmnïau rheoli eiddo, asiantau tai, gwestai, cymdeithasau tai, a chwmnïau rheoli cyfleusterau. P\'un a oes gennych ardd breifat neu\'n rheoli sawl eiddo, gallaf ddarparu gwasanaethau garddio wedi\'u teilwra i\'ch anghenion.',
        category: 'Cyffredinol'
      },
      // Tree Surgery Questions
      {
        question: 'Oes angen caniatâd i dynnu coed?',
        answer: 'Mae\'n dibynnu ar y goeden a\'ch lleoliad. Gall rhai coed fod wedi\'u diogelu gan Orchmynion Cadw Coed (TPOs) neu fod mewn ardaloedd cadwraeth. Byddaf yn gwirio hyn i chi a helpu gydag unrhyw geisiadau angenrheidiol cyn dechrau\'r gwaith.',
        category: 'Llawfeddygaeth Coed'
      },
      {
        question: 'Faint mae llawfeddygaeth coed yn ei gostio?',
        answer: 'Mae costau llawfeddygaeth coed yn amrywio yn dibynnu ar faint, lleoliad, a chymhlethdod y gwaith. Rwy\'n darparu dyfynbris am ddim, manwl ar ôl asesu eich sefyllfa benodol. Mae ffactorau yn cynnwys maint y goeden, mynediad, a gofynion gwaredu.',
        category: 'Llawfeddygaeth Coed'
      },
      {
        question: 'Allwch chi dynnu coed mawr yn ddiogel?',
        answer: 'Ydy, mae gen i\'r arbenigedd ac offer i dynnu coed o bob maint yn ddiogel. Ar gyfer coed mawr, rwy\'n defnyddio technegau ac offer arbenigol i sicrhau tynnu diogel heb ddifrod i\'ch eiddo.',
        category: 'Llawfeddygaeth Coed'
      },
      {
        question: 'Beth sy\'n digwydd i\'r bonyn goeden ar ôl tynnu?',
        answer: 'Rwy\'n cynnig gwasanaethau malurio bonion i dynnu bonion coed yn llwyr. Mae hyn yn atal ail-dyfu ac yn caniatáu i chi ddefnyddio\'r lle ar gyfer dibenion eraill. Gallaf hefyd adael y bonyn os ydych yn ei ddewis, neu ei dorri i lefel y ddaear.',
        category: 'Llawfeddygaeth Coed'
      },
      {
        question: 'Pa mor hir mae llawfeddygaeth coed yn ei gymryd?',
        answer: 'Mae\'r parhad yn dibynnu ar faint a chymhlethdod y gwaith. Gall gwaith tocio bach gymryd ychydig o oriau, tra gall tynnu coed mawr gymryd diwrnod cyfan. Byddaf yn rhoi amserlen realistig i chi pan fyddaf yn dyfynnu.',
        category: 'Llawfeddygaeth Coed'
      },
      // Fencing Questions
      {
        question: 'Pa mor hir mae gosod ffens yn ei gymryd?',
        answer: 'Mae amser gosod yn amrywio yn seiliedig ar y math o ffens a\'r hyd. Mae ffens gardd nodweddiadol (20-30 metr) fel arfer yn cymryd 1-2 ddiwrnod. Byddaf yn darparu amserlen benodol pan fyddaf yn asesu eich prosiect.',
        category: 'Ffensio'
      },
      {
        question: 'Pa fathau o ddeunyddiau ffensio ydych chi\'n gweithio gyda nhw?',
        answer: 'Rwy\'n gweithio gydag amrywiaeth eang o ddeunyddiau gan gynnwys pren, metel, vinyl, a ffensio cyfansawdd. Gallaf gynghori ar yr opsiwn gorau ar gyfer eich anghenion, gan ystyried ffactorau fel gwydnwch, cynnal a chadw, a chyllideb.',
        category: 'Ffensio'
      },
      {
        question: 'Oes angen caniatâd cynllunio ar gyfer ffensys?',
        answer: 'Nid yw\'r rhan fwyaf o ffensys gardd yn gofyn am ganiatâd cynllunio, ond mae cyfyngiadau uchder (fel arfer 2 fetr). Os yw eich ffens yn uwch neu mewn ardal gadwraeth, byddaf yn eich helpu i wirio\'r gofynion.',
        category: 'Ffensio'
      },
      {
        question: 'Allwch chi atgyweirio ffensys presennol?',
        answer: 'Ydy, gallaf atgyweirio\'r rhan fwyaf o fathau o ffensio. Byddaf yn asesu\'r difrod a chynghori ar y dull gorau - p\'un ai atgyweiriad syml ydyw neu a fyddai disodli yn fwy cost-effeithiol yn y tymor hir.',
        category: 'Ffensio'
      },
      {
        question: 'Pa mor hir fydd fy ffens newydd yn para?',
        answer: 'Mae hoedran ffens yn dibynnu ar y deunydd a chynnal a chadw. Mae ffensys pren fel arfer yn para 10-15 mlynedd gyda thriniaeth briodol, tra gall ffensys metel a vinyl bara 20+ mlynedd. Byddaf yn cynghori ar gynnal a chadw i fwyhau hirhoedledd.',
        category: 'Ffensio'
      },
      // Pressure Washing Questions
      {
        question: 'A yw golchi pwysedd yn ddiogel ar gyfer pob arwyneb?',
        answer: 'Rwy\'n defnyddio lefelau pwysedd addas ar gyfer arwynebau gwahanol i osgoi difrod. Rwy\'n brofiadol o lanhau drwyfeydd, patios, deciau, ffensys, ac arwynebau awyr agored eraill yn ddiogel ac yn effeithiol.',
        category: 'Golchi Pwysedd'
      },
      {
        question: 'Pa mor aml ddylwn i olchi pwysedd fy nhrwyf/patio?',
        answer: 'Rwy\'n argymell golchi pwysedd drwyfeydd a phatios yn flynyddol i gynnal eu golwg ac atal croniad alga/mwsogl. Gall ardaloedd â llawer o draffig neu rai mewn lleoliadau cysgodol fod angen glanhau mwy aml.',
        category: 'Golchi Pwysedd'
      },
      {
        question: 'Ydych chi\'n defnyddio cynhyrchion glanhau eco-gyfeillgar?',
        answer: 'Ydy, rwy\'n defnyddio atebion glanhau sy\'n gyfeillgar i\'r amgylchedd lle bo\'n bosibl. Gallaf drafod eich dewisiadau a defnyddio cynhyrchion sy\'n ddiogel i\'ch gardd, anifeiliaid anwes, a\'r amgylchedd.',
        category: 'Golchi Pwysedd'
      },
      {
        question: 'Pa mor hir mae golchi pwysedd yn ei gymryd i sychu?',
        answer: 'Mae amser sychu yn dibynnu ar amodau tywydd a math arwyneb. Fel arfer, mae arwynebau\'n sych o fewn 2-4 awr mewn tywydd da. Byddaf yn cynghori ar yr amser gorau i amserlennu\'r gwaith.',
        category: 'Golchi Pwysedd'
      },
      {
        question: 'All golchi pwysedd ddifrodi fy mhlanhigion?',
        answer: 'Rwy\'n gofalu am ddiogelu eich planhigion yn ystod golchi pwysedd. Rwy\'n cuddio planhigion cyfagos a defnyddio lefelau pwysedd addas i osgoi difrod. Byddaf yn trafod unrhyw bryderon cyn dechrau\'r gwaith.',
        category: 'Golchi Pwysedd'
      }
    ],
    contactText: 'Dal gennych gwestiynau?',
    contactLink: 'Cysylltwch â mi'
  }
} 